import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Home, MapPin, Compass, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";
import PageSEO from "@/components/PageSEO";

const NotFound = () => {
  const { t } = useTranslation();
  const { dir } = useLocale();

  // Immediately set 404 title for crawlers
  useEffect(() => {
    document.title = "404 - Page Not Found | Travel Boost";
    
    // Set HTTP status hint for crawlers (meta tag approach)
    const statusMeta = document.createElement("meta");
    statusMeta.name = "prerender-status-code";
    statusMeta.content = "404";
    document.head.appendChild(statusMeta);

    return () => {
      document.head.removeChild(statusMeta);
    };
  }, []);

  return (
    <Layout>
      <PageSEO 
        title="הדף לא נמצא (404)"
        description="הדף שחיפשתם לא נמצא. חזרו לדף הבית או השתמשו בכלי הניווט."
        noindex={true}
      />
      <section className="min-h-[80vh] flex items-center justify-center py-16 bg-gradient-to-b from-primary/5 via-background to-background" dir={dir}>
        <div className="container max-w-lg text-center px-4">
          <div className="relative mb-10 animate-fade-in-up">
            <div className="relative mx-auto w-64 h-64 md:w-72 md:h-72">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-coral/10 rounded-full" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="w-32 h-32 bg-card rounded-full shadow-xl flex items-center justify-center border-4 border-primary/20">
                    <Compass className="h-16 w-16 text-primary animate-pulse" />
                  </div>
                  <div className="absolute -top-4 -right-4 bg-coral text-white p-2 rounded-full shadow-lg animate-bounce">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div className="absolute -bottom-2 -left-6 bg-primary text-white p-2 rounded-full shadow-lg float">
                    <Plane className="h-5 w-5" />
                  </div>
                </div>
              </div>
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground font-bold text-3xl px-6 py-3 rounded-2xl shadow-lg">
                404
              </div>
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold mb-4 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            {t("not_found.title")}
          </h1>
          <p className="text-lg text-muted-foreground mb-8 animate-fade-in-up leading-relaxed" style={{ animationDelay: "0.2s" }}>
            {t("not_found.subtitle")}
            <br />
            {t("not_found.subtitle_2")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
            <Button asChild size="lg" className="gap-2 btn-glow bg-primary hover:bg-primary/90 text-lg px-8 h-14 min-w-[200px]">
              <Link to="/">
                <Home className="h-5 w-5" />
                {t("not_found.back_home")}
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="gap-2 text-lg px-8 h-14 min-w-[200px]">
              <Link to="/tools">
                <Compass className="h-5 w-5" />
                {t("not_found.to_tools")}
              </Link>
            </Button>
          </div>

          {/* SEO: Helpful links for crawlers */}
          <nav className="mt-12 pt-8 border-t border-border/50" aria-label="Site navigation">
            <p className="text-sm text-muted-foreground mb-4">דפים פופולריים:</p>
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <Link to="/destinations/israel" className="text-primary hover:underline">יעדים בישראל</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/destinations/world" className="text-primary hover:underline">יעדים בעולם</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/tools" className="text-primary hover:underline">כלי נסיעות</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/blog" className="text-primary hover:underline">בלוג</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/contact" className="text-primary hover:underline">צור קשר</Link>
            </div>
          </nav>
        </div>
      </section>
    </Layout>
  );
};

export default NotFound;